/**
 * compressImage — client-side JPEG/PNG compression via Canvas.
 *
 * Goal: keep uploads under ~800 KB so the user's phone-camera HEIC/JPEG
 * (often 3–8 MB) doesn't blow past the proxy's payload limit and the page
 * preview renders fast.
 *
 * Strategy:
 *  - Files ≤ targetMaxBytes are returned untouched.
 *  - PDFs and non-image types are returned untouched (no canvas handling).
 *  - Images are decoded via `createImageBitmap`, drawn into an offscreen
 *    canvas resized to `maxDimension` (long edge), and re-encoded as JPEG
 *    at decreasing quality steps until either the size goal is met or
 *    quality drops below `minQuality`.
 *  - The original `name` is preserved (with extension swapped to .jpg).
 *
 * Returns a Promise resolving to either the compressed File or the original
 * file on any failure path — so consumers can always upload the result.
 */
export async function compressImage(file, {
    targetMaxBytes = 800 * 1024,
    maxDimension = 1800,
    minQuality = 0.55,
} = {}) {
    if (!file) return file;
    if (!file.type.startsWith("image/")) return file;
    if (file.size <= targetMaxBytes) return file;

    try {
        const bitmap = await createImageBitmap(file);
        const longEdge = Math.max(bitmap.width, bitmap.height);
        const scale = longEdge > maxDimension ? maxDimension / longEdge : 1;
        const w = Math.round(bitmap.width * scale);
        const h = Math.round(bitmap.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(bitmap, 0, 0, w, h);
        bitmap.close?.();

        let quality = 0.85;
        let blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
        while (blob && blob.size > targetMaxBytes && quality > minQuality) {
            quality = Math.max(minQuality, quality - 0.1);
            // eslint-disable-next-line no-await-in-loop
            blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
        }
        if (!blob) return file;
        const baseName = (file.name || "image").replace(/\.[^.]+$/, "");
        return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
    } catch {
        // Any failure → fall back to original (preserve user data)
        return file;
    }
}
